import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { staffScheduleAPI, positionsCatalogAPI } from './actions';

const StaffPositionModal = ({ 
  isOpen, onClose, positionData, onSuccess, kindergartenId,
  groupsCount,
  workMode,
  specialization 
}) => {
  const [formData, setFormData] = useState({
    positionId: '',
    positionName: '',
    positionType: '',
    units: '',
    salary: '',
    description: ''
  });

  const [positionsCatalog, setPositionsCatalog] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({
    maxUnits: 0,
    currentTotal: 0,
    remainingUnits: 0,
    isValid: true,
    message: ''
  });
  const [selectedPositionInfo, setSelectedPositionInfo] = useState(null);

  // Загрузка справочника должностей
  useEffect(() => {
    const loadPositionsCatalog = async () => {
      try {
        const positions = await positionsCatalogAPI.getPositions();
        setPositionsCatalog(positions);
        setFilteredPositions(positions.slice(0, 10));
      } catch (error) {
        console.error('Ошибка загрузки справочника:', error);
        //toast.error('Не вдалося завантажити довідник посад');
      }
    };
    
    if (isOpen) {
      loadPositionsCatalog();
      setValidation({ maxUnits: 0, currentTotal: 0, remainingUnits: 0, isValid: true, message: '' });
    }
  }, [isOpen]);

  // Инициализация формы
  useEffect(() => {
    if (positionData && isOpen) {
      setFormData({
        positionId: positionData.positionId?._id || positionData.positionId || '',
        positionName: positionData.position || positionData.positionName || '',
        positionType: positionData.type || positionData.positionType || '',
        units: positionData.units || '',
        salary: positionData.salary || '',
        description: positionData.description || ''
      });
      
      if (positionData.positionId) {
        const selectedPos = positionsCatalog.find(p => p._id === positionData.positionId);
        if (selectedPos) {
          setSelectedPositionInfo(selectedPos);
          setSearchTerm(selectedPos.name);
        }
      }
    } else if (isOpen) {
      setFormData({
        positionId: '',
        positionName: '',
        positionType: '',
        units: '',
        salary: '',
        description: ''
      });
      setSelectedPositionInfo(null);
      setSearchTerm('');
    }
    setShowSearchResults(false);
  }, [positionData, isOpen, positionsCatalog]);

  // Поиск должностей
  const handleSearch = (term) => {
    setSearchTerm(term);
    
    if (term.length < 2) {
      setFilteredPositions(positionsCatalog.slice(0, 10));
      setShowSearchResults(false);
      return;
    }
    
    const filtered = positionsCatalog.filter(position =>
      position.name.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredPositions(filtered.slice(0, 10));
    setShowSearchResults(true);
  };

  // Выбор должности из справочника
  const handlePositionSelect = (position) => {
    const positionType = getPositionTypeFromReference(position);
    
    setFormData(prev => ({
      ...prev,
      positionId: position._id,
      positionName: position.name,
      positionType: positionType
    }));
    
    setSelectedPositionInfo(position);
    setSearchTerm(position.name);
    setShowSearchResults(false);
    
    // Автоматически проверяем максимальные единицы при выборе должности
    if (formData.units) {
      checkMaxUnits(position._id, formData.units);
    }
  };

  // Получение типа должности из additionalFields
  const getPositionTypeFromReference = (position) => {
    if (!position.additionalFields) return 'Не визначено';
    
    const typeValue = position.additionalFields.get 
      ? position.additionalFields.get('type')
      : position.additionalFields.type;
    
    const typeMap = {
      'administrative': 'Адміністративний',
      'pedagogical': 'Педагогічний',
      'technical': 'Технічний',
      'service': 'Обслуговуючий'
    };
    
    return typeMap[typeValue] || 'Не визначено';
  };

  // Проверка максимального количества единиц
  const checkMaxUnits = async (positionId, units) => {
    if (!positionId || !units || !kindergartenId) return;
    
    try {
      //const result = await staffScheduleAPI.checkMaxRules({groupsCount, workMode, specialization, positionId});
      const result = await staffScheduleAPI.checkMaxUnits(kindergartenId, {units: +units, calculationData: { groupsCount, workMode, specialization}, positionId});
      
      setValidation(result);
      
      /*
      setValidation({
        maxUnits: result,
        currentTotal: 0,
        remainingUnits:0,
        isValid: result >= units,
        message: result >= units ? '': "Перевищено ліміт посад, створених за правилами"
      });
      */

    } catch (error) {
      console.error('Ошибка проверки максимальных единиц:', error);
      // В случае ошибки сбрасываем валидацию
      setValidation({
        maxUnits: 0,
        currentTotal: 0,
        remainingUnits: 0,
        isValid: true,
        message: ''
      });
    }
  };

  // Дебаунс для проверки максимальных единиц
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.positionId && formData.units && kindergartenId) {
        checkMaxUnits(formData.positionId, formData.units);
      } else {
        setValidation({
          maxUnits: 0,
          currentTotal: 0,
          remainingUnits: 0,
          isValid: true,
          message: ''
        });
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [formData.positionId, formData.units, kindergartenId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        positionId: formData.positionId,
        units: formData.units,
        salary: formData.salary,
        description: formData.description
      };

      if (positionData) {
        await staffScheduleAPI.updatePosition(kindergartenId, positionData.id, submitData);
      } else {
        await staffScheduleAPI.createPosition(kindergartenId, submitData);
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {positionData ? 'Редагувати посаду' : 'Додати нову посаду'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Поиск должности из справочника */}
          <Form.Group className="mb-3">
            <Form.Label>Посада *</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => setShowSearchResults(true)}
                placeholder="Пошук посади в довіднику..."
                required
              />
              
              {showSearchResults && filteredPositions.length > 0 && (
                <div 
                  className="position-absolute w-100 bg-white border mt-1 rounded shadow-lg"
                  style={{ zIndex: 1060, maxHeight: '200px', overflowY: 'auto' }}
                >
                  {filteredPositions.map(position => (
                    <div
                      key={position._id}
                      className="p-2 border-bottom hover-bg-light cursor-pointer"
                      onClick={() => handlePositionSelect(position)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="fw-medium">{position.name}</div>
                      {position.description && (
                        <small className="text-muted">{position.description}</small>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {selectedPositionInfo && (
              <div className="mt-2 p-2 bg-light rounded">
                <small>
                  <strong>Обрана посада:</strong> {selectedPositionInfo.name}
                  {selectedPositionInfo.description && (
                    <> - {selectedPositionInfo.description}</>
                  )}
                </small>
              </div>
            )}
          </Form.Group>

          {/* Тип должности (только просмотр) */}
          <Form.Group className="mb-3">
            <Form.Label>Тип посади</Form.Label>
            <Form.Control
              type="text"
              value={formData.positionType}
              readOnly
              placeholder="Тип буде визначено автоматично після вибору посади"
              className="bg-light"
            />
            <Form.Text className="text-muted">
              Тип посади визначається автоматично з довідника
            </Form.Text>
          </Form.Group>

          {/* Количество штатных единиц с валидацией */}
          <Form.Group className="mb-3">
            <Form.Label>Кількість штатних одиниць *</Form.Label>
            <Form.Control
              type="number"
              name="units"
              value={formData.units}
              onChange={handleChange}
              required
              min="0"
              step="0.5"
              placeholder="0.5, 1.0, 1.5..."
              isInvalid={!validation.isValid && validation.maxUnits > 0}
              disabled={!formData.positionId}
            />
            
            {/* Валидация максимальных единиц */}
            {validation.maxUnits > 0 && (
              <div className="mt-2">
                <div className={`alert ${validation.isValid ? 'alert-success' : 'alert-danger'} py-2`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <strong>
                        {validation.isValid ? '✓ Доступно' : '✗ Перевищено'} 
                      </strong>
                      : {+validation.currentTotal} (поточні) + {+formData.units || 0} (нові) = {+validation.currentTotal + parseFloat(formData.units || 0)} / {validation.maxUnits} (макс.)
                    </span>
                    <Badge bg={validation.isValid ? "success" : "danger"}>
                      Макс: {validation.maxUnits}
                    </Badge>
                  </div>
                  {!validation.isValid && (
                    <div className="mt-1 small">
                      {validation.message}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {!formData.positionId && (
              <Form.Text className="text-muted">
                Спочатку оберіть посаду з довідника для перевірки доступної кількості штатних одиниць
              </Form.Text>
            )}
          </Form.Group>

          {/* Оклад */}
          <Form.Group className="mb-3">
            <Form.Label>Оклад (грн)</Form.Label>
            <Form.Control
              type="number"
              name="salary"
              value={formData.salary}
              onChange={handleChange}
              min="0"
              placeholder="Введіть розмір окладу"
            />
          </Form.Group>

          {/* Описание должности */}
          <Form.Group className="mb-3">
            <Form.Label>Опис посади</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Введіть опис обов'язків та вимог"
            />
          </Form.Group>

          {/* Информация о данных для расчета */}
          <div className="bg-light p-3 rounded small">
            <strong>Інформація для розрахунку:</strong>
            <div className="mt-1">
              • Дані про кількість груп та режим роботи будуть отримані автоматично з моделі садика
              <br/>
              • Максимальна кількість штатних одиниць розраховується на основі правил та характеристик садика
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Скасувати
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || !formData.positionId || !formData.units || (validation.maxUnits > 0 && !validation.isValid)}
          >
            {loading ? 'Збереження...' : (positionData ? 'Зберегти зміни' : 'Додати посаду')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default StaffPositionModal;